import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { useAuthStore } from "@/stores/auth-store";
import { DashboardLayout } from "@/layouts/dashboard-layout";
import { LoginPage } from "@/features/auth/login-page";

const OverviewPage = lazy(() => import("@/features/dashboard/overview-page").then((m) => ({ default: m.OverviewPage })));
const MarketsPage = lazy(() => import("@/features/markets").then((m) => ({ default: m.MarketsPage })));
const p = (name: string) => lazy(() => import("@/features/pages").then((m) => ({ default: (m as any)[name] })));
const PayinPage = p("PayinPage");
const PayoutPage = p("PayoutPage");
const SettlementsPage = p("SettlementsPage");
const WalletPage = p("WalletPage");
const CredentialsPage = p("CredentialsPage");
const DocsPage = p("DocsPage");
const SettingsPage = p("SettingsPage");
const WebhooksPage = p("WebhooksPage");
const DisputesPage = lazy(() => import("@/features/disputes").then((m) => ({ default: m.DisputesPage })));
const SupportPage = lazy(() => import("@/features/support").then((m) => ({ default: m.SupportPage })));

function ProtectedRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
}
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="text-sm text-muted-foreground">Loading…</div>}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          { path: "/", element: <Navigate to="/overview" replace /> },
          { path: "/overview", element: <Lazy><OverviewPage /></Lazy> },
          { path: "/markets", element: <Lazy><MarketsPage /></Lazy> },
          { path: "/orders/payin", element: <Lazy><PayinPage /></Lazy> },
          { path: "/orders/payout", element: <Lazy><PayoutPage /></Lazy> },
          { path: "/settlements", element: <Lazy><SettlementsPage /></Lazy> },
          { path: "/wallet", element: <Lazy><WalletPage /></Lazy> },
          { path: "/withdrawals", element: <Navigate to="/settlements" replace /> },
          { path: "/credentials", element: <Lazy><CredentialsPage /></Lazy> },
          { path: "/docs", element: <Lazy><DocsPage /></Lazy> },
          { path: "/settings", element: <Lazy><SettingsPage /></Lazy> },
          { path: "/webhooks", element: <Lazy><WebhooksPage /></Lazy> },
          { path: "/disputes", element: <Lazy><DisputesPage /></Lazy> },
          { path: "/support", element: <Lazy><SupportPage /></Lazy> },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/overview" replace /> },
]);
